/* 20/05/2024 - Khang - Ignore for separate deployment */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

import { ViewLayout, AdvanceStack, OtherLayout } from './components';
import { RibbonBar, RibbonDivider } from 'common/components';
import ManageSharingSection from './components/sections/shared/ManageSharingSection';
import DetailSection from './components/sections/home-grid/DetailSection';
import ProductHierarchy from './components/controls/product-full/ProductHierarchy';
import HistorySection from './components/sections/product/HistorySection';
import ProductDetailView from './components/controls/product-full/ProductDetailView';
import DetailQaSpec from './components/controls/product-full/DetailQaSpec';
import QASpecAssignSupplier from './components/controls/product-full/QASpecAssignSupplier';
import QaEvaluationForm from './components/controls/product-full/QaEvaluationForm';
import ProductPackaging from './components/controls/product-full/ProductPackaging';

import HierarchyActions from './components/controls/product-full/HierarchyActions';
import ProductFullAction from './components/controls/product-full/ProductFullAction';
import * as actionsGlobal from '@redux/global/actions';
import { useGetProductFullView, useUserInfo } from 'hooks';
import { useOpenReportingResult } from 'hooks/useOpenReportingResult';
import { useCheckPermissionOR, hasPermission } from 'hooks/useCheckPermissions';
import { useFolderByLevel } from 'hooks/useFolderByLevel';
import { useInitialWorkflowQaSpec } from 'hooks/useInitialWorkflowQaSpec';
import QaSpecHistoryIcon from 'assets/traceable-comment/QaSpecHistoryIcon';
import RibbonButton from 'common/components/button/RibbonButton';
import { useGetQaSpecHistory } from './components/controls/product-full/hooks/useGetQaSpecHistory';
import { allowMemberUseEvalForm } from 'utils/checkIsOwner';
import DetailsQASpecHistory from './components/controls/product-full/DetailsQASpecHistory';
import QaSpecTemplateAction from './components/controls/product-full/QaSpecTemplateAction';
import * as qaSpecServices from 'services/qaSpec';

const ProductFullViewRibbon = ({
  isHaveAddOpenItem = true,
  productId,
  shouldShowPackaging = true,
}) => {
  const dispatch = useDispatch();
  const { userInfo } = useUserInfo();

  const { keepSetting, keepResultGridPagination } = useOpenReportingResult();
  const checkPermission = useCheckPermissionOR();

  const { invalidateQaSpecHistory } = useGetQaSpecHistory(productId, false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);

  const [isBulkUpdateVisible, setIsBulkUpdateVisible] = useState(false);
  const [isQaSpecTemplateVisible, setIsQaSpecTemplateVisible] = useState(false);

  const toggleBulkUpdateVisible = () => {
    setIsBulkUpdateVisible((prev) => !prev);
  }

  const showHistoryModal = () => {
    invalidateQaSpecHistory();
    setIsHistoryModalVisible(true);
  };
  const closeHistoryModal = () => {
    setIsHistoryModalVisible(false);
  };

  const hasQaSpecPermission = checkPermission([
    [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.QA_SPECIFICATION],
  ]);

  const hasPermissionUploadQaSpecTemplate = hasPermission(ABILITY_SUBJECT.UPLOAD_QA_SPECIFICATION_TEMPLATE)

  const hasQaSpecPackingPermission = checkPermission([
    [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.QA_SPECIFICATION],
    [ABILITY_ACTION.EDIT, ABILITY_SUBJECT.QA_SPECIFICATION],
  ]);

  const hasEvaluationFormPermission = checkPermission([
    [ABILITY_ACTION.VIEW, ABILITY_SUBJECT.EVALUATION_FORM],
  ]);

  const { productFull } = useGetProductFullView({ productId });

  const { shouldKeepFolderBreadcrumb, keepFolderBreadcrumb } =
    useFolderByLevel();

  const closeDetailCallback = () => {
    keepSetting();
    keepResultGridPagination();

    //* keep folder breadcrumb when close folder item detail
    shouldKeepFolderBreadcrumb && keepFolderBreadcrumb(true);
  };

  //* Reset not go back to members route.
  //* In case user go form member full view -> company's product.
  useEffect(() => {
    dispatch(actionsGlobal.resetProdAssetsToMember());
  }, [dispatch]);

  const { checkInitialWorkflowQaSpecRibbon } = useInitialWorkflowQaSpec();
  const isInitialWorkflowQaSpecRibbon = checkInitialWorkflowQaSpecRibbon();

  useEffect(() => {
    const checkTemplateVisibility = async () => {
      const response = await qaSpecServices.checkQaSpecTemplateVisibility({ productId: [productId] });

      if (response) {
        setIsQaSpecTemplateVisible(response.data)
      }
    }

    checkTemplateVisibility();
  }, [])

  return (
    <>
      <RibbonBar>
        <ManageSharingSection />
        <DetailSection />
        <ViewLayout disabled={true} />
        <RibbonDivider />
        <ProductDetailView productId={productId} />

        <ProductFullAction productFull={productFull} productId={productId} />

        <RibbonDivider />
        {shouldShowPackaging && hasQaSpecPackingPermission ? (
          <ProductPackaging productFull={productFull} />
        ) : null}
        <ProductHierarchy productId={productId} />
        {/* {isAllowEditProduct ? <Subscription productId={productId} /> : null} */}

        <RibbonDivider />

        <HierarchyActions productId={productId} />

        <HistorySection />

        {hasQaSpecPermission && <DetailQaSpec productData={productFull} />}

        {isInitialWorkflowQaSpecRibbon && (
          <>
            <QASpecAssignSupplier productData={productFull} />
            <RibbonDivider />
          </>
        )}

        {hasEvaluationFormPermission &&
          allowMemberUseEvalForm(productFull?.ownerId) && (
            <>
              <QaEvaluationForm productData={productFull} />
              <RibbonDivider />
            </>
          )}

        {hasQaSpecPermission && (
          <>
            <RibbonButton
              icon={<QaSpecHistoryIcon />}
              label='QA Spec Status History'
              onClick={() => {
                showHistoryModal();
              }}
            />
            <RibbonDivider />
          </>
        )}

        <AdvanceStack isDisabled={true} />

        {hasPermissionUploadQaSpecTemplate && isQaSpecTemplateVisible && (
          <QaSpecTemplateAction productFull={productFull} productId={productId} />
        )}

        <OtherLayout
          ribbonSide='right'
          ribbonType='products'
          isHaveAddOpenItem={isHaveAddOpenItem}
          closeDetailCallback={closeDetailCallback}
        />
      </RibbonBar>
      <DetailsQASpecHistory
        visible={isHistoryModalVisible}
        onClose={closeHistoryModal}
        productId={productId}
      />

      <DetailsQASpecHistory
        visible={isBulkUpdateVisible}
        onClose={toggleBulkUpdateVisible}
        productId={productId}
      />
    </>
  );
};

export default ProductFullViewRibbon;
